import React, { useState, useEffect } from "react";
import { Scale, Star, ChevronLeft, X } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useHeaderHeight } from "../hooks/useHeaderHeight";
import { AIResponse } from "../services/aiService";

function LegalSearchResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [aiResponse, setAiResponse] = useState<AIResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const headerHeight = useHeaderHeight();

  useEffect(() => {
    // Check if we received AI response via navigation state
    if (location.state?.query && location.state?.aiResponse) {
      setSearchQuery(location.state.query);
      setAiResponse(location.state.aiResponse);
    } else {
      // Fallback to URL params for backward compatibility
      setSearchQuery(searchParams.get("q") || "arrest without warrant maryland");
    }
  }, [location, searchParams]);

  const legalAuthorities = [
    {
      title: "Md. Code Ann., Crim. Proc. § 2-202 (Current)",
      description:
        "A police officer may arrest without a warrant any person who the officer has probable cause to belie...",
      type: "Maryland State Law",
    },
    {
      title: "Riley v. California, 573 U.S. 373 (2014)",
      description:
        "Police generally need warrant to search digital information on cell phone seized incident to arrest....",
      type: "Federal Case",
    },
    {
      title: "Carroll v. United States, 267 U.S. 132 (1925)",
      description:
        "Vehicles may be searched without warrant if probable cause exists, due to mobility and reduced expec...",
      type: "Federal Case",
    },
    {
      title: "Arizona v. Gant, 556 U.S. 332 (2009)",
      description:
        "Search of vehicle incident to arrest only permitted if arrestee within reach of vehicle or evidence ...",
      type: "Federal Case",
    },
    {
      title: "Md. Code Ann., Family Law § 4-513 (Current)",
      description:
        "Mandatory arrest requirements and procedures for domestic violence incidents in Maryland....",
      type: "Maryland State Law",
    },
  ];

  const handleBackToHome = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-900 relative">
      {/* Side borders */}
      <div className="absolute left-0 top-0 w-1 h-full bg-slate-600"></div>
      <div className="absolute right-0 top-0 w-1 h-full bg-slate-700"></div>

      {/* Status/Progress Bars */}
      <div className="w-full">
        <div className="bg-blue-700/60 px-6 py-2.5 flex items-center justify-center space-x-3">
          <Star className="h-5 w-5 text-white/70" />
          <span className="text-white/70 text-sm font-medium text-center">
            Analyzing with NLP and searching comprehensive legal database...
          </span>
        </div>
      </div>

      {/* Navigation Link - Now with working sticky positioning */}
      <div
        className="bg-slate-900 py-2 border-b border-slate-700 sticky z-10"
        style={{ top: `${headerHeight}px` }}
      >
        <div className="">
          <Button
            variant="ghost"
            onClick={handleBackToHome}
            className="text-command-light-blue hover:text-command-light-blue/80 hover:bg-transparent p-0 h-auto font-normal text-base flex flex-row items-center"
          >
            <ChevronLeft className="h-5 w-5" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="px-6 mt-4">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-white mb-1">
            Legal Search Results
          </h2>
          <p className="text-sm text-white/70 mb-2">
            NLP-enhanced Federal & Maryland law database
          </p>
          <p className="text-xs text-command-light-blue">
            Search: "{searchQuery}" - Found 9 legal authorities
          </p>
        </div>

        {/* AI Legal Analysis Card */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Scale className="h-6 w-6 text-blue-400" />
              <h3 className="text-lg font-bold text-command-light-blue">
                AI Legal Analysis
              </h3>
              {aiResponse && (
                <span className="text-xs text-green-400 bg-green-400/20 px-2 py-1 rounded">
                  Live AI Response
                </span>
              )}
            </div>

            <div className="space-y-4 text-white/70">
              {aiResponse ? (
                <>
                  <div>
                    <h4 className="mb-2 text-sm font-semibold text-white">
                      AI Analysis for "{searchQuery}"
                    </h4>
                  </div>
                  
                  <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-600">
                    <div className="text-sm text-white/90 whitespace-pre-wrap">
                      {aiResponse.choices[0]?.message?.content || "No response content available"}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-slate-600">
                    <div className="text-xs text-white/50">
                      Model: {aiResponse.model}
                    </div>
                    <div className="text-xs text-white/50">
                      Tokens: {aiResponse.usage?.total_tokens || 0}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Scale className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-white/60">
                    No AI analysis available. Using fallback content for "{searchQuery}".
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Search Tabs */}
        <div className="flex space-x-2 my-6">
          <Button
            variant="default"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-l-lg flex items-center space-x-2"
          >
            <Scale className="h-4 w-4" />
            <span>All Law</span>
          </Button>
          <Button
            variant="outline"
            className="bg-slate-800 border-slate-600 text-white hover:bg-slate-700 px-4 py-2 flex items-center space-x-2"
          >
            <span className="text-lg">⚖️</span>
            <span className="text-white/70">Federal</span>
          </Button>
          <Button
            variant="outline"
            className="bg-slate-800 border-slate-600 text-white hover:bg-slate-700 px-4 py-2 rounded-r-lg flex items-center space-x-2"
          >
            <span className="text-lg">🔵</span>
            <span className="text-white/70">Maryland</span>
          </Button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <span className="text-white/70">🔍</span>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {}}
            className="w-full bg-slate-800 border text-sm border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Search legal database..."
          />
        </div>

        {/* Search Results */}
        <div className="space-y-4">
          {/* First Result - Maryland State Law */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">⚖️</span>
                  <span className="bg-green-600/50 text-white text-xs px-2 py-1 rounded-md">
                    Maryland State Law
                  </span>
                  <span className="text-white/25 text-xs ">NLP Score: 99</span>
                </div>
                <div className="text-right text-xs text-white/70">
                  <div>Relevance: 99%</div>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-white mb-2">
                Criminal Procedure - Arrests Without Warrant
              </h3>

              <p className="text-sm italic text-white/80 mb-3">
                Md. Code Ann., Crim. Proc. § 2-202
              </p>

              <p className="text-sm text-white/70 mb-4">
                A police officer may arrest without a warrant any person who the
                officer has probable cause to believe has committed a felony or
                misdemeanor in the officer's presence.
              </p>

              <div className="flex items-center justify-between">
                <span className="bg-slate-700 text-white/50 text-xs px-2 py-1 rounded">
                  Criminal Procedure
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setIsDialogOpen(true)}
                    className="text-white/70 text-xs text-green-400 hover:text-green-300 cursor-pointer transition-colors"
                  >
                    View Full Code →
                  </button>
                  <span className="text-white/70 text-xs text-blue-400">
                    State Archive →
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Second Result - Case Law */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">⚖️</span>
                  <span className="bg-blue-600/40 text-white text-xs px-2 py-1 rounded-md">
                    Case Law
                  </span>
                  <span className="text-white/25 text-xs ">NLP Score: 90</span>
                </div>
                <div className="text-right text-xs text-white/70">
                  <div>Relevance: 93%</div>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-white mb-2">
                Riley v. California - Cell Phone Searches
              </h3>

              <p className="text-sm italic text-white/80 mb-3">
                Riley v. California, 573 U.S. 373 (2014)
              </p>

              <p className="text-sm text-white/70 mb-4">
                Police generally need warrant to search digital information on
                cell phone seized incident to arrest.
              </p>

              <div className="flex items-center justify-between">
                <span className="bg-slate-600 text-white/70 text-xs px-2 py-1 rounded">
                  Digital Privacy
                </span>
                <div className="flex space-x-2 text-blue-400 text-xs">
                  Case Details →
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Legal Code Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-600 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="relative">
            <div className="flex flex-row">
              <div className="w-3 h-3 bg-green-500 rounded-sm mt-2"></div>
              <div className="flex flex-col items-start ml-4">
                <p className="text-xs font-bold text-white">
                  Criminal Procedure - Arrests Without Warrant
                </p>
                <p className="text-sm text-white/70">
                  Md. Code Ann., Crim. Proc. § 2-202
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsDialogOpen(false)}
              className="absolute top-0 right-0 text-white/70 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </DialogHeader>

          <div className="bg-black rounded-lg p-4 font-mono text-sm">
            <div className="space-y-2">
              <div className="text-green-400">
                Connected to Command Legacy Backend - 16 Legal
              </div>
              <div className="text-white/80">Loading legal database...</div>
              <div className="text-white/80">
                Searching for: "arrest without warrant maryland"
              </div>
              <div className="text-white/80">Found 9 relevant authorities</div>
              <div className="text-white/80">Analyzing with NLP...</div>
              <div className="text-green-400">✓ Analysis complete</div>
              <div className="text-white/80">
                on cell phone seized incident to arrest.
              </div>
              <div className="text-blue-400">Case Details →</div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-600">
            <Button
              onClick={() => setIsDialogOpen(false)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LegalSearchResults;
